import axios from 'axios';
import { createAsyncThunk, isFulfilled, isPending, isRejected } from '@reduxjs/toolkit';
import { PeriodicType } from 'app/shared/model/enumerations/periodicType';
import { cleanEntity } from 'app/shared/util/entity-utils';
import { IQueryParams, createEntitySlice, EntityState, serializeAxiosError } from 'app/shared/reducers/reducer.utils';
import { ISensorData, defaultValue } from 'app/shared/model/sensor-data.model';

const initialState: EntityState<ISensorData> = {
  loading: false,
  errorMessage: null,
  entities: [],
  entity: defaultValue,
  updating: false,
  totalItems: 0,
  updateSuccess: false,
};

const apiUrl = 'api/sensor-data';

// Actions

export const getEntities = createAsyncThunk('sensorData/fetch_entity_list', async ({ page, size, sort }: IQueryParams) => {
  const requestUrl = `${apiUrl}${sort ? `?page=${page}&size=${size}&sort=${sort}&` : '?'}cacheBuster=${new Date().getTime()}`;
  return axios.get<ISensorData[]>(requestUrl);
});

export const getEntity = createAsyncThunk(
  'sensorData/fetch_entity',
  async (id: string | number) => {
    const requestUrl = `${apiUrl}/${id}`;
    return axios.get<ISensorData>(requestUrl);
  },
  { serializeError: serializeAxiosError }
);

export const createEntity = createAsyncThunk(
  'sensorData/create_entity',
  async (entity: ISensorData, thunkAPI) => {
    const result = await axios.post<ISensorData>(apiUrl, cleanEntity(entity));
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const updateEntity = createAsyncThunk(
  'sensorData/update_entity',
  async (entity: ISensorData, thunkAPI) => {
    const result = await axios.put<ISensorData>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const partialUpdateEntity = createAsyncThunk(
  'sensorData/partial_update_entity',
  async (entity: ISensorData, thunkAPI) => {
    const result = await axios.patch<ISensorData>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const deleteEntity = createAsyncThunk(
  'sensorData/delete_entity',
  async (id: string | number, thunkAPI) => {
    const requestUrl = `${apiUrl}/${id}`;
    const result = await axios.delete<ISensorData>(requestUrl);
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const getTruckPosition = (id: string | number) => {
    const requestUrl = `${apiUrl}/truck/${id}/position/last`;
    return axios.get(requestUrl)
};

export const getTruckRoute = (id: string | number, periodic: string, dateStart: string, dateEnd: string) => {
  
  let requestUrl = null;
  if(periodic === PeriodicType.CUSTOM){
    requestUrl = `${apiUrl}/truck/${id}/position/route${periodic ? `?periodic=${periodic}&startDate=${dateStart}&endDate=${dateEnd}` : ''}`
  }else{
    requestUrl = `${apiUrl}/truck/${id}/position/route${periodic ? `?periodic=${periodic}` : ''}`
  }
  return axios.get(requestUrl);
};

// slice

export const SensorDataSlice = createEntitySlice({
  name: 'sensorData',
  initialState,
  extraReducers(builder) {
    builder
      .addCase(getEntity.fulfilled, (state, action) => {
        state.loading = false;
        state.entity = action.payload.data;
      })
      .addCase(deleteEntity.fulfilled, state => {
        state.updating = false;
        state.updateSuccess = true;
        state.entity = {};
      })
      .addMatcher(isFulfilled(getEntities), (state, action) => {
        return {
          ...state,
          loading: false,
          entities: action.payload.data,
          totalItems: parseInt(action.payload.headers['x-total-count'], 10),
        };
      })
      .addMatcher(isFulfilled(createEntity, updateEntity, partialUpdateEntity), (state, action) => {
        state.updating = false;
        state.loading = false;
        state.updateSuccess = true;
        state.entity = action.payload.data;
      })
      .addMatcher(isPending(getEntities, getEntity), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.loading = true;
      })
      .addMatcher(isPending(createEntity, updateEntity, partialUpdateEntity, deleteEntity), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.updating = true;
      });
  },
});

export const { reset } = SensorDataSlice.actions;

// Reducer
export default SensorDataSlice.reducer;
